import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { Button, Text, Label, Input, Select, Option, FlexBox, Title, AnalyticalTable, Dialog, Wizard, WizardStep, Icon, BusyIndicator} from '@ui5/webcomponents-react';
import Navbar from './Navbar';
import SideMenu from './SideMenu';
import { rightAlignedNumberStyle, formattedNumberCell, formatNumberWithComma, parseToNumber } from '../utils/validator';
import { v4 as uuidv4 } from 'uuid';
import useAutoTranslate from '../hooks/useAutoTranslate';
import useBlobUploader from '../utils/blobUploader'; // Import the blob uploader hook

const ASNDraft = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [asnDetail, setAsnDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [newShipmentInstructions, setNewShipmentInstructions] = useState('');
    const [newDeliveryDate, setNewDeliveryDate] = useState('');
    const [newPackageQty, setNewPackageQty] = useState('');
    const [newTotalVolume, setNewTotalVolume] = useState(0);
    const [newTotalWeight, setNewTotalWeight] = useState(0);
    const [newChargeType, setNewChargeType] = useState('');
    const [newTransportMedium, setNewTransportMedium] = useState('');
    const [purchaseOrder, setPurchaseOrder] = useState(null);
    const [bultos, setBultos] = useState([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedBulto, setSelectedBulto] = useState(null);
    const [combinedPackingLists, setCombinedPackingLists] = useState([]);
    const [newShipmentType, setNewShipmentType] = useState('');
    const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
    const [errorDialogMessage, setErrorDialogMessage] = useState('');
    const [disponibles, setDisponibles] = useState([]);
    const [deletedPackageIds, setDeletedPackageIds] = useState([]);
    const [selectedStep, setSelectedStep] = useState(0);
    const [newShipmentDate, setNewShipmentDate] = useState(asnDetail?.shipmentDate || '');
    const [newDescription, setNewDescription] = useState(asnDetail?.description || '');
    const [newSlipNumber, setNewSlipNumber] = useState(asnDetail?.slipNumber || '');
    const [attachments, setAttachments] = useState({});
    const [attachmentSections, setAttachmentSections] = useState({
      header: { isOpen: false, isRequired: false },
      safetySheet: { isOpen: false, isRequired: false },
      dispatchGuide: { isOpen: false, isRequired: true },
      deliveryNotice: { isOpen: false, isRequired: true },
      indivisibleCertificate: { isOpen: false, isRequired: false },
      other: { isOpen: false, isRequired: false }
    });
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
    const { uploadFile } = useBlobUploader(); // Initialize the blob uploader hook
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, isUploading: false });

    const toggleAttachmentSection = (sectionKey) => {
      setAttachmentSections(prev => ({
        ...prev,
        [sectionKey]: { ...prev[sectionKey], isOpen: !prev[sectionKey].isOpen }
      }));
    };

    const handleAttachmentUpload = async (sectionKey, file) => {
      try {
        setUploadProgress({ current: 0, total: 1, isUploading: true }); // Start upload

        // Use the actual upload function to Azure Blob Storage
        const uploadedUrl = await new Promise((resolve, reject) => {
          uploadFile(file, resolve, reject); // Pass resolve and reject to the uploader
        });

        setAttachments(prev => ({
          ...prev,
          [sectionKey]: { file, filePath: uploadedUrl, uploadDate: new Date().toISOString() }
        }));
      } catch (error) {
        console.error('Error uploading file:', error);
        // Fallback: store without URL for now (or handle the error more robustly)
        setAttachments(prev => ({
          ...prev,
          [sectionKey]: { file, filePath: URL.createObjectURL(file), uploadDate: new Date().toISOString() }
        }));
      } finally {
        setUploadProgress({ current: 1, total: 1, isUploading: false }); // End upload
      }
    };

    const removeAttachment = (sectionKey) => {
      setAttachments(prev => {
        const updated = { ...prev };
        delete updated[sectionKey];
        return updated;
      });
    };

    const { autoT } = useAutoTranslate();

    // Opciones para Tipo de Carga
    const chargeTypeOptions = [
        { key: '', text: 'Seleccionar' },
        { key: 'A granel', text: 'Carga General' },
        { key: 'Caja Carton', text: 'Producto Quimico' },
        { key: 'con Antigolpes', text: 'Sustancias Peligrosas' },
    ];

    // Opciones para Tipo de Transporte
    const transportMediumOptions = [
        { key: '', text: 'Seleccionar' },
        { key: 'Camioneta', text: 'Camioneta' },
        { key: 'Furgon o Utilitario', text: 'Furgon o Utilitario' },
        { key: 'Camion menor a 1 Tonelada', text: 'Equipo 3/4' },
        { key: 'Camion 1,5 Toneladas', text: 'Equipo 1,5 Toneladas' },
        { key: 'Camion 3,5 Toneladas', text: 'Equipo 3,5 Toneladas' },
        { key: 'Camion 15 Toneladas', text: 'Equipo 15 Toneladas' },
        { key: 'Camion 17 Toneladas', text: 'Equipo 17 Toneladas' },
        { key: 'Camion 28 Toneladas', text: 'Equipo 28 Toneladas' },
    ];

    // Opciones para Tipo de Envío
    const shipmentTypeOptions = [
        { key: '', text: 'Seleccionar' },
        { key: 'A granel', text: 'A granel' },
        { key: 'Carton', text: 'Caja cartón' },
        { key: 'con Antigolpes', text: 'Con antigolpes' },
        { key: 'Caja de Madera con Antigolpes', text: 'Caja de madera con antigolpes' },
        { key: 'Embalaje con marca Fragil', text: 'Embalaje con marca "Frágil"' },
        { key: 'Embalaje con marca No Mojar', text: 'Embalaje con marca "No Mojar"' },
        { key: 'Embalaje con marca Peligroso', text: 'Embalaje con marca "Peligroso"' },
        { key: 'Embalaje estándar del Proveedor', text: 'Embalaje estándar del proveedor' },
        { key: 'Embalaje Impermeable', text: 'Embalaje impermeable' },
        { key: 'Embalaje de Exportación', text: 'Embalaje de exportación' },
        { key: 'Jaba de Madera', text: 'Jaba de madera' },
        { key: 'Embalaje ver Clausula', text: 'Embalaje ver cláusula' },
        { key: 'Caja de Madera', text: 'Caja de madera' },
        { key: 'Marcado Mantener Aislado', text: 'Marcado "Mantener Aislado"' },
        { key: 'Pallet', text: 'Pallet' },
        { key: 'Container', text: 'Container' },
    ];

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const fetchASNDetail = async () => {
        try {
            const response = await axiosInstance.get(`asn-documents/draft/${id}`);
    
            if (!response || !response.data || !response.data.data) {
                throw new Error("Invalid response format");
            }
    
            const asnData = response.data.data;
            console.log("ASN Detail Response: ", asnData);
    
            setAsnDetail(asnData);
            setNewShipmentInstructions(asnData.shipmentInstructions);
            setNewDeliveryDate(asnData.deliveryDate);
            setNewPackageQty(asnData.packageQty);
            setNewTotalVolume(asnData.totalVolume);
            setNewTotalWeight(asnData.totalWeight);
            setNewChargeType(asnData.chargeType || '');
            setNewTransportMedium(asnData.transportMedium || '');
            setNewShipmentType(asnData.shipmentType || '')
            setNewShipmentDate(asnData.shipmentDate || '');
            setNewDescription(asnData.description || '');
            setNewShipmentInstructions(asnData.shipmentInstructions || '');
            setNewSlipNumber(asnData.slipNumber || '');
    
            // Inicializar bultos con los datos de asnDetail.packages
            if (Array.isArray(asnData.packages)) {
                const initializedBultos = asnData.packages.map(pkg => {
                    const firstPackingList = pkg.packingLists[0] || {}; // Obtener el primer packingList
                    const altura = firstPackingList.height || 0; // Asignar altura del primer packingList
                    const largo = firstPackingList.length || 0; // Asignar largo del primer packingList
                    const ancho = firstPackingList.width || 0; // Asignar ancho del primer packingList

                    const bulto = {
                        id: pkg.id,
                        packageNumber: pkg.packageNumber,
                        weight: pkg.weight || 0,
                        altura: altura,
                        largo: largo,
                        ancho: ancho,
                        volumen: calcularVolumen(altura, largo, ancho) // Calcular volumen al inicializar
                    };
                    return bulto;
                });
                setBultos(initializedBultos);
            } else {
                setBultos([]);
            }
    
            if (asnData.purchaseOrderId) {
                await fetchPurchaseOrder(asnData.purchaseOrderId);
            } else {
                console.warn("No purchaseOrderId found in ASN data");
            }
    
        } catch (err) {
            console.error('Error fetching ASN details:', err);
            setError('Error fetching ASN details');
        } finally {
            setLoading(false);
        }
    };
    const handleStepChange = (event) => {
        const nextStep = event.detail.step - 1;
        setSelectedStep(nextStep);
    };
    
    const fetchPurchaseOrder = async (purchaseOrderId) => {
        if (!purchaseOrderId) return;
        try {
            // Primera llamada para obtener la orden de compra
            const response = await axiosInstance.get(`purchase-orders/${purchaseOrderId}`);

            // Segunda llamada a purchase-orders/order-number/
            const orderNumberResponse = await axiosInstance.get(`purchase-orders/order-number/${response.data.data.orderNumber}`);
            setPurchaseOrder(orderNumberResponse.data.data);
            console.log("Order Number Details ", orderNumberResponse.data.data);
            // Aquí puedes manejar los datos de la segunda llamada como desees
        } catch (err) {
            setError('Error fetching PurchaseOrder');
        }
    };

      const itemColumns = [
        { Header: 'Item PO', accessor: 'lineId' },
        { Header: 'Código de material', accessor: 'materialCode' },
        { Header: 'Nombre', accessor: 'description' },
        { Header: 'Unidad', accessor: 'unit' },
        {
          Header: 'Precio Unitario',
          accessor: 'pricePerUnit',
          Cell: ({ value }) => (
            <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(value)}</Text>
          ),
          hAlign: "End"
        },
        {
          Header: 'Cantidad Pendiente',
          accessor: 'quantityPending',
          Cell: ({ value }) => (
            <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(value)}</Text>
          ),
          hAlign: "End"
        },
        {
          Header: 'Cantidad',
          accessor: 'quantity',
          Cell: ({ row }) => {
            const lineId = row.original.lineId;
            const existingItem = asnDetail.items.find(item => item.lineId === lineId);
            const initialQuantity = existingItem?.quantity || 0;
            const [tempQuantity, setTempQuantity] = useState(formatNumberWithComma(initialQuantity));
            const [originalQuantity, setOriginalQuantity] = useState(initialQuantity);
            useEffect(() => {
                // actualizar si cambia el existingItem desde afuera
                setTempQuantity(formatNumberWithComma(initialQuantity));
                setOriginalQuantity(initialQuantity);
              }, [existingItem]);

            return (
                <Input
                type="text"
                    value={tempQuantity}
                onInput={(e) => {
                    const raw = e.target.value;
                    const cleaned = raw.replace(/[^0-9,]/g, ''); // Permitir solo dígitos y coma
                    setTempQuantity(cleaned);
                }}
                onBlur={() => {
                    const parsedValue = parseToNumber(tempQuantity);
                    const formatted = formatNumberWithComma(parsedValue);
                    const previousValue = parseToNumber(originalQuantity);
                    const difference = parsedValue - previousValue;

                    const quantityPending = purchaseOrder.items.find(item => item.lineId === row.original.lineId)?.quantityPending || 0;
                    const updatedPendingQuantity = quantityPending - difference;

                    const updatedItems = asnDetail.items.map(item => {
                    if (item.lineId === lineId) {
                        return {
                        ...item,
                        quantity: parsedValue
                        };
                                }
                                return item;
                            });

                    const updatedItems2 = purchaseOrder.items.map(item => {
                    if (item.lineId === lineId) {
                        return {
                        ...item,
                        quantityPending: updatedPendingQuantity
                        };
                    }
                    return item;
                    });

                            setAsnDetail({ ...asnDetail, items: updatedItems });
                    setPurchaseOrder({ ...purchaseOrder, items: updatedItems2 });

                    setDisponibles(prev =>
                    prev.map(item => {
                        if (item.lineId === row.original.lineId) {
                        return {
                            ...item,
                            quantityLeft: item.quantityLeft + difference
                        };
                        }
                        return item;
                    }).filter(item => item.quantityLeft > 0)
                    );

                    if (!existingItem && parsedValue > 0) {
                    const orderItem = purchaseOrder.items.find(item => item.lineId === lineId);
                            const newItem = {
                                lineId: lineId,
                        materialCode: orderItem?.materialCode || '',
                                description: orderItem?.description || '',
                                unit: orderItem?.unit || null,
                        pricePerUnit: orderItem?.pricePerUnit || 0,
                        quantityPending: parsedValue,
                        asnSentQuantity: parsedValue,
                        quantity: parsedValue
                    };
                     setAsnDetail(prev => ({
                         ...prev,
                        items: [...prev.items, newItem]
                     }));
                    }
                     
                    setOriginalQuantity(parsedValue);
                    setTempQuantity(formatted); // Mostrar formateado
                }}
                style={{ width: '100px', textAlign: 'right' }}
                />

            );
          },
          hAlign: "End"
        },
        {
            Header: 'Precio Total',
            accessor: 'totalPrice',
            Cell: ({ row }) => {
                const pricePerUnit = row.original.pricePerUnit || 0;
                const quantity = asnDetail.items.find(item => item.lineId === row.original.lineId)?.quantity || 0;
                const totalPrice = (pricePerUnit * quantity);
                return (
                  <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(totalPrice)}</Text>
                );
            }
        },
    ];

    const calcularVolumen = (altura, largo, ancho) => {
        // Asegurarse de que todos los valores son números
        const alturaNum = parseFloat(altura) || 0;
        const largoNum = parseFloat(largo) || 0;
        const anchoNum = parseFloat(ancho) || 0;
        
        // Calcular el volumen como número
        const volumen = alturaNum * largoNum * anchoNum;
        
        // Devolver el valor numérico, no la cadena formateada
        return volumen;
    };

    const handleBultoUpdate = (bultoNumber, field, value) => {
        setBultos(prev => {
            const updatedBultos = prev.map(bulto => {
                if (bulto.packageNumber === bultoNumber) {
                    const parsedValue = parseToNumber(value);
                    const newBulto = { ...bulto, [field]: parsedValue };
                    
                    // Recalcular volumen si se modificó alguna dimensión
                    if (['altura', 'largo', 'ancho'].includes(field)) {
                        const altura = field === 'altura' ? parsedValue : bulto.altura;
                        const largo = field === 'largo' ? parsedValue : bulto.largo;
                        const ancho = field === 'ancho' ? parsedValue : bulto.ancho;
                        
                        // Calcular el volumen como valor numérico
                        newBulto.volumen = calcularVolumen(altura, largo, ancho);
                        
                        console.log(`Actualizando bulto ${bultoNumber}:`, { 
                            altura, 
                            largo, 
                            ancho, 
                            nuevoVolumen: newBulto.volumen 
                        });
                    }
                    
                    return newBulto;
                }
                return bulto;
            });
            
            return updatedBultos;
        });
    };


  
    
    const buildRequestBody = () => {
      return {
          id: asnDetail?.id || null,
          asnDate: asnDetail?.asnDate || new Date().toISOString(),
          asnStatus: asnDetail?.asnStatus || "Draft",
          asnNumber: asnDetail?.asnNumber || `ASN-${Date.now()}`,
          purchaseOrderId: purchaseOrder?.id,
          
          // Campos de fechas
          deliveryDate: newDeliveryDate || asnDetail?.deliveryDate,
          deliveryTime: newDeliveryDate ? newDeliveryDate + "T00:00:00" : asnDetail?.deliveryTime,
          shipmentDate: newShipmentDate || asnDetail?.shipmentDate,
          shipmentTime: newShipmentDate ? newShipmentDate + "T00:00:00" : asnDetail?.shipmentTime,
          
          // Campos de medidas
          totalWeight: bultos.reduce((total, bulto) => total + (bulto.weight || 0), 0),
          totalVolume: bultos.reduce((total, bulto) => {
              const volumen = parseToNumber(bulto.volumen || 0);
              return total + volumen;
          }, 0),
          
          // Campos de selección
          transportMedium: newTransportMedium || asnDetail?.transportMedium,
          shipmentType: newShipmentType || asnDetail?.shipmentType,
          chargeType: newChargeType || asnDetail?.chargeType,
          
          // Campos de texto
          description: newDescription || asnDetail?.description || "",
          vehiclePlate: asnDetail?.vehiclePlate || "",
          shipmentInstructions: newShipmentInstructions || asnDetail?.shipmentInstructions || "",
          slipNumber: newSlipNumber || asnDetail?.slipNumber || "",
          eeNumber: asnDetail?.eeNumber || `EE-${Math.floor(Math.random() * 1000)}`,
          
          // Campos de cantidad
          packageQty: bultos.length,
          
          // Metadatos
          createdBy: asnDetail?.createdBy || "315f91a9-b4ea-467e-929d-feb6bf447fb2",
          modifiedBy: asnDetail?.modifiedBy || "315f91a9-b4ea-467e-929d-feb6bf447fb2",
          createdDate: asnDetail?.createdDate || new Date().toISOString(),
          modifiedDate: new Date().toISOString(),
          
          // Items
          items: asnDetail?.items.map(item => ({
              id: item.id || uuidv4(),
              asnDocumentId: asnDetail?.id || null,
              lineId: item.lineId,
              materialCode: item.materialCode,
              description: item.description,
              quantity: item.quantity,
              unit: item.unit,
              pricePerUnit: item.pricePerUnit,
              totalPrice: (item.pricePerUnit * item.quantity),
              receivedQuantity: item.receivedQuantity || null,
              quantityReverse: item.quantityReverse || null,
              quantityPending: item.quantityPending || null,
              asnSentQuantity: item.asnSentQuantity || null,
              positionPo: item.positionPo || null
          })) || [],
          
          // Paquetes
          packages: bultos.map(bulto => ({
              id: bulto.id || null,
              asnDocumentId: asnDetail?.id || null,
              packageNumber: bulto.packageNumber || null,
              weight: bulto.weight || 0,
              dimensions: `${bulto.altura || 0}x${bulto.largo || 0}x${bulto.ancho || 0}`,
              volume: parseToNumber(bulto.volumen || 0),
              length: bulto.largo || 0,
              width: bulto.ancho || 0,
              height: bulto.altura || 0,
              packingLists: combinedPackingLists
                  .filter(item => item.packageNumber === bulto.packageNumber)
                  .map(packingList => ({
                      id: packingList.id || null,
                      itemDescription: packingList.itemDescription,
                      asnDocumentId: asnDetail?.id || null,
                      asnPackageId: packingList.asnPackageId || null,
                      packageNumber: bulto.packageNumber || null,
                      packageCodebars: packingList.packageCodebars || "1",
                      weight: bulto.weight || 0,
                      volume: parseToNumber(bulto.volumen || 0),
                      length: bulto.largo || 0,
                      width: bulto.ancho || 0,
                      height: bulto.altura || 0,
                      quantity: packingList.quantity || 0
                  })) || []
          })) || []
      };
  };

    const handleDeleteBulto = (bulto) => {
        // Verificar si el packageNumber contiene "Bulto"
        if (!bulto.packageNumber.includes("Bulto")) {
            // Guardar el ID del bulto eliminado
            setDeletedPackageIds(prev => [...prev, bulto.id]);
        }

        // Actualizar quantityLeft para los packingLists asociados
        const packingListsToUpdate = combinedPackingLists.filter(item => item.packageNumber === bulto.packageNumber);
        
        setDisponibles(prev => {
            return prev.map(item => {
                const packingListItem = packingListsToUpdate.find(pl => 
                    pl.lineId === item.lineId
                );
                if (packingListItem) {
                    return { 
                        ...item, 
                        quantityLeft: item.quantityLeft + packingListItem.quantity // Aumentar quantityLeft
                    };
                }
                return item;
            }).concat(
                packingListsToUpdate
                    .filter(pl => !prev.some(item => item.lineId === pl.lineId)) // Filtrar materiales no existentes
                    .map(pl => ({
                        lineId: pl.lineId,
                        materialCode: pl.itemDescription,
                        quantityLeft: pl.quantity // Agregar nuevo material con su cantidad
                    }))
            );
        });

        // Eliminar el bulto de la lista
        setBultos(prev => prev.filter(item => item.packageNumber !== bulto.packageNumber));
    };

    const handleSaveDraft = async () => {
      try {
          // Realizar la solicitud DELETE para cada ID de paquete eliminado
          for (const packageId of deletedPackageIds) {
              await axiosInstance.delete(`/packages/draft/${packageId}`);
          }

          const requestBody = buildRequestBody();
          const jsonRequestBody = JSON.stringify(requestBody);

          await axiosInstance.put(`/asn-documents/draft/${id}`, jsonRequestBody, {
              headers: {
                  'Content-Type': 'application/json'
              }
          });
          console.log('Attachments:', attachments);
          if (Object.keys(attachments).length > 0) {  
            console.log('Attachments: SI');
            setErrorDialogMessage("Los cambios han sido guardados excepto los archivos adjuntos que no se pueden guardar en borrador.");
          } else {
              console.log('Attachments: NO');
              setErrorDialogMessage("El borrador se ha guardado exitosamente.");
          }
          setIsSuccessDialogOpen(true); // Open success dialog on successful save
      } catch (error) {
          console.error('Error saving draft:', error);
          setErrorDialogMessage("Error al guardar el borrador. Por favor intente nuevamente.");
          setIsErrorDialogOpen(true);
      }
  };
  
  const handleSaveNotification = async () => {
    const errorMessage = validateForm();
    if (errorMessage) {
        setErrorDialogMessage(errorMessage);
        setIsErrorDialogOpen(true);
        return;
    }

    try {
        // Realizar la solicitud DELETE para cada ID de paquete eliminado
        for (const packageId of deletedPackageIds) {
            await axiosInstance.delete(`/packages/draft/${packageId}`);
        }

        // Construir la payload similar a tu ejemplo funcional
        const packagesPayload = bultos.map(bulto => ({
            packageNumber: bulto.packageNumber,
            weight: bulto.weight,
            volume: parseToNumber(bulto.volumen),
            dimensions: `${bulto.altura}x${bulto.largo}x${bulto.ancho} m`,
            packingLists: combinedPackingLists
                .filter(item => item.packageNumber === bulto.packageNumber)
                .map(packingList => ({
                    packageNumber: bulto.packageNumber,
                    packageCodebars: bulto.packageNumber,
                    rfidEpc: "",
                    weight: bulto.weight,
                    volume: parseToNumber(bulto.volumen),
                    length: bulto.largo,
                    width: bulto.ancho,
                    height: bulto.altura,
                    itemDescription: packingList.itemDescription,
                    quantity: packingList.quantity
                }))
        }));

        const payload = {
            asnNumber: `ASN-${Date.now()}`,
            purchaseOrderId: purchaseOrder?.id,
            asnDate: new Date().toISOString(),
            deliveryDate: newDeliveryDate || asnDetail?.deliveryDate,
            deliveryTime: newDeliveryDate ? newDeliveryDate + "T00:00:00" : asnDetail?.deliveryTime,
            shipmentDate: newShipmentDate || asnDetail?.shipmentDate,
            shipmentTime: newShipmentDate ? newShipmentDate + "T00:00:00" : asnDetail?.shipmentDate,
            totalWeight: bultos.reduce((sum, b) => sum + (b.weight || 0), 0),
            totalVolume: bultos.reduce((sum, b) => sum + parseToNumber(b.volumen || 0), 0),
            shipmentType: newShipmentType,
            transportMedium: newTransportMedium,
            chargeType: newChargeType,
            packageQty: bultos.length,
            vehiclePlate: asnDetail?.vehiclePlate || "",
            shipmentInstructions: newShipmentInstructions || "",
            asnStatus: "Pending",
            rampAssignmentStatus: "PENDING",
            eeNumber: asnDetail?.eeNumber || `EE-${Math.floor(Math.random() * 1000)}`,
            items: asnDetail?.items.map(item => ({
                lineId: item.lineId,
                materialCode: item.materialCode,
                description: item.description,
                quantity: item.quantity,
                unit: item.unit,
                pricePerUnit: item.pricePerUnit,
                positionPo: item.positionPo || null
            })),
            packages: packagesPayload,
            createdBy: asnDetail?.createdBy || "315f91a9-b4ea-467e-929d-feb6bf447fb2"
        };

        console.log('Payload a enviar:', JSON.stringify(payload, null, 2));

        const response = await axiosInstance.post('asn-documents', payload);

        // Upload attachments to Azure Blob after ASN creation
        const asnDocumentId = response.data.data.id;
        const attachmentKeys = Object.keys(attachments);
        for (let i = 0; i < attachmentKeys.length; i++) {
          const sectionKey = attachmentKeys[i];
          const attachment = attachments[sectionKey];

          const attachmentPayload = {
            asnDocumentId: asnDocumentId, // Use the newly created ASN document ID
            attachmentName: attachment.file.name || 'archivo_sin_nombre',
            attachmentType: "asn-attachments",
            filePath: `/attachments/${attachment.file.name}`, // URL temporal
            mandatory: attachmentSections[sectionKey]?.isRequired || false,
            createdDate: attachment.uploadDate || new Date().toISOString()
          };

          try {
            await axiosInstance.post('attachments', attachmentPayload);
            console.log(`Archivo ${attachment.file.name} subido exitosamente.`);
          } catch (error) {
            console.error(`Error enviando adjunto ${sectionKey}:`, error);
            throw new Error(`Error al cargar archivo ${attachment.file.name || sectionKey}`);
          }
        }

        // 3. Si se creó correctamente, eliminar el borrador
        if (response.data.code === "SUCCESS" && id) {
          try {
              await axiosInstance.delete(`/asn-documents/draft/${id}`);
              console.log(`Borrador ${id} eliminado exitosamente`);
          } catch (deleteError) {
              console.error('Error al eliminar el borrador:', deleteError);
          }
      }
      navigate(`/asn/${asnDocumentId}`);

    } catch (error) {
        console.error('Error saving notification:', error);
        setErrorDialogMessage("Error al guardar la notificación. Por favor intente nuevamente.");
        setIsErrorDialogOpen(true);
    }
};

    const handleOpenDialog = (bulto) => {
        setSelectedBulto(bulto);
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedBulto(null);
    };

    const handleAddBulto = () => {
        const newBulto = {
            packageNumber: `Bulto ${bultos.length + 1}`,
            weight: 0,
            altura: 0,
            largo: 0,
            ancho: 0,
            volumen: 0,
        };
        console.log('Creando nuevo bulto:', newBulto);
        console.log('Lista actual de bultos:', bultos);
        setBultos(prev => {
            const updated = [...prev, newBulto];
            console.log('Lista actualizada de bultos:', updated);
            return updated;
        });
    };

    const handleAddMaterial = (material) => {
        if (!selectedBulto) {
            console.error("No hay bulto seleccionado.");
            return;
        }
    
        const materialsInSelectedBulto = combinedPackingLists.filter(
            item => item.packageNumber === selectedBulto.packageNumber
        );
    
        const exists = materialsInSelectedBulto.some(
            item => item.lineId === material.lineId
        );
        if (exists) {
            console.error(`la position ya existe en el bulto ${selectedBulto.packageNumber}.`);
            return;
        }
    
        // Usar la cantidad disponible, o mínimo 1
        const quantityToAdd = material.quantityLeft > 0 ? material.quantityLeft : 1;
    
        const newMaterial = {
            lineId: material.lineId,
            itemDescription: material.materialCode,
            quantity: quantityToAdd,
            packageNumber: selectedBulto.packageNumber,
        };
    
        setCombinedPackingLists(prev => [...prev, newMaterial]);
    
        // Actualizar la cantidad faltante
        setDisponibles(prev => {
            return prev.map(item => {
                if (item.lineId === material.lineId) {
                    return {
                        ...item,
                        quantityLeft: item.quantityLeft - quantityToAdd
                    };
                }
                return item;
            })
        });
    };
    

    useEffect(() => {
        fetchASNDetail();
    }, [id]);

    useEffect(() => {
        if (asnDetail && purchaseOrder) {
            console.log("Fetching packing lists and materials...");

            // Agrupar packingLists por lineId o itemDescription
            const packingListMap = {};
            asnDetail.packages.forEach(pkg => {
                pkg.packingLists.forEach(item => {
                    const key = item.lineId;
                    if (!packingListMap[key]) {
                        packingListMap[key] = 0; // Inicializar si no existe
                    }
                    packingListMap[key] += item.quantity; // Sumar cantidades
                });
            });

            console.log("Packing list aggregated:", packingListMap);

            // Convertir el objeto a un array
            const aggregatedPackingLists = Object.entries(packingListMap).map(([key, quantity]) => ({
                lineId: key,
                quantity
            }));

            console.log("Aggregated packing lists:", aggregatedPackingLists);

            // Filtrar los materiales disponibles para agregar
            const materialsToAdd = asnDetail.items.map(item => {
                const packingItem = aggregatedPackingLists.find(packingItem => 
                    packingItem.lineId === item.lineId
                );
                
                // Log para ver el material actual
                console.log(`Processing material: ${item.materialCode}, lineId: ${item.lineId}`);

                const totalPackingQuantity = packingItem ? packingItem.quantity : 0; // Total de packingLists para este material
                const totalAsnQuantity = item.quantity; // Cantidad en ASN

                // Log para ver las cantidades
                console.log(`Total Packing Quantity for ${item.materialCode}: ${totalPackingQuantity}`);
                console.log(`Total ASN Quantity for ${item.materialCode}: ${totalAsnQuantity}`);

                // Calcular quantityLeft como la suma de packing quantity menos la cantidad en ASN
                const quantityLeft = totalPackingQuantity - totalAsnQuantity;

                // Log para ver el resultado del cálculo
                console.log(`Quantity Left for ${item.materialCode}: ${quantityLeft}`);

                return {
                    lineId: item.lineId,
                    materialCode: item.materialCode,
                    quantityLeft: quantityLeft // No filtrar aquí, lo haremos más adelante
                };
            });

            // Invertir el signo de todos los elementos (negativos a positivos y positivos a negativos)
            const invertedMaterials = materialsToAdd
                .map(item => ({
                    lineId: item.lineId,
                    materialCode: item.materialCode,
                    quantityLeft: -item.quantityLeft // Invertir el signo
                }));

            console.log("Materials with inverted signs:", invertedMaterials);
            // Actualizar el estado de disponibles
            setDisponibles(invertedMaterials);

            const newCombinedPackingLists = asnDetail.packages?.flatMap(pkg => pkg.packingLists) || [];
            setCombinedPackingLists(newCombinedPackingLists);
        }
    }, [asnDetail, purchaseOrder]);

const validateForm = () => {
    // Validaciones existentes
    if (!newShipmentInstructions) {
        return "Las instrucciones de despacho son obligatorias.";
    }
    if (!newDeliveryDate) {
        return "La fecha de entrega es obligatoria.";
    }
    if (!newShipmentDate) {
        return "La fecha de salida es obligatoria.";
    }
    if (bultos.length === 0) {
        return "Debes agregar al menos un bulto.";
    }
    if (!newTotalVolume || newTotalVolume <= 0) {
        return "El volumen debe ser un número positivo.";
    }
    if (!newTotalWeight || newTotalWeight <= 0) {
        return "El peso debe ser un número positivo.";
    }
    if (!newChargeType) {
        return "Debes seleccionar un tipo de carga.";
    }
    if (!newTransportMedium) {
        return "Debes seleccionar un medio de transporte.";
    }
    if (!newShipmentType) {
        return "Debes seleccionar un tipo de envío.";
    }
    if (!newDescription) {
        return "La descripción es obligatoria.";
    }
    if (!newSlipNumber) {
        return "La guía de despacho es obligatoria.";
    }

    // Validaciones de cantidades
    for (const item of purchaseOrder.items) {
        if (item.quantityPending < 0) {
            return "Ninguna cantidad pendiente puede ser negativa.";
        }
    }

    const totalBultosVolume = bultos.reduce((total, bulto) => total + (parseToNumber(bulto.volumen) || 0), 0);
    if (totalBultosVolume !== parseToNumber(newTotalVolume)) {
        return "El volumen total de los bultos debe ser igual al volumen del Notificación.";
    }

    const totalBultosWeight = bultos.reduce((total, bulto) => total + (bulto.weight || 0), 0);
    if (totalBultosWeight !== parseToNumber(newTotalWeight)) {
        return "El peso total de los bultos debe ser igual al peso de la Notificación.";
    }

    // Validación de materiales
    const hasNonZeroQuantityLeft = disponibles.some(item => item.quantityLeft !== 0);
    if (hasNonZeroQuantityLeft) {
        return "Faltan o sobran materiales en los bultos.";
    }

    if (new Date(newDeliveryDate) < new Date().setHours(0, 0, 0, 0)) {
        return "La fecha de entrega debe ser igual o superior al día de hoy.";
    }
    if (new Date(newShipmentDate) < new Date().setHours(0, 0, 0, 0)) {
        return "La fecha de salida debe ser igual o superior al día de hoy.";
    }

    return null; // Si todas las validaciones pasan
};

    // Actualizados los manejadores para los totales para que sólo cambien 
    // cuando el usuario modifica directamente estos campos
    const handleTotalVolumeChange = (event) => {
        const value = event.target.value.replace(/[^0-9,]/g, '');
        const parsedValue = parseToNumber(value);
        setNewTotalVolume(parsedValue);
    };

    const handleTotalWeightChange = (event) => {
        const value = event.target.value.replace(/[^0-9,]/g, '');
        const parsedValue = parseToNumber(value);
        setNewTotalWeight(parsedValue);
    };
    // Calculamos los totales de los bultos pero sólo para mostrarlos, 
    // no para actualizar los campos de entrada
    const totalBultosVolume = bultos.reduce((total, bulto) => total + (parseToNumber(bulto.volumen) || 0), 0);
    const totalBultosWeight = bultos.reduce((total, bulto) => total + (bulto.weight || 0), 0);

    if (loading) return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <BusyIndicator active="true" size="L" text={autoT("Cargando Borrador...")} />
      </div>
    );
    if (error) return <div>{autoT("Error")}: {error}</div>;
  

    return (
        <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            minHeight: '100vh' 
        }}>
            <Navbar toggleSidebar={toggleSidebar} />
            <div style={{ 
                display: 'flex',
                flex: 1,
                marginTop: '68px'
            }}>
                <SideMenu isOpen={sidebarOpen} />

                <div style={{ 
                    flex: 1,
                    marginLeft: sidebarOpen ? '250px' : '0',
                    transition: 'margin-left 0.3s',
                    padding: '20px'
                }}>
                 <h2>{autoT(`Borrador N° ${asnDetail?.asnNumber} Orden de Compra N° ${purchaseOrder?.orderNumber}`)}</h2>

                <Wizard 
              onStepChange={handleStepChange} 
              style={{ 
                height: 'calc(100vh - 88px)',
                width: '100%'
              }}
            > 
             <WizardStep
                icon="edit"
                titleText={autoT("Editar datos")}
                selected={selectedStep === 0}
              >

                    
                    <Title level="H5" style={{ marginBottom: '1rem' }}>{autoT('Información de envío')}</Title>
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(3, 1fr)', 
                        gap: '1rem',
                        marginBottom: '1rem'
                    }}>
                        {/* Fecha de salida */}
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT("Fecha de salida")}:</Label>
                            <Input 
                                type="date" 
                                value={newShipmentDate} 
                                onInput={(e) => setNewShipmentDate(e.target.value)}
                            />
                        </div>
                        {/* Fecha de entrega */}
                         <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT("Fecha de entrega")}:</Label>
                            <Input 
                                type="date" 
                                value={newDeliveryDate} 
                                onInput={(e) => setNewDeliveryDate(e.target.value)}
                            />
                        </div>

                        {/* Descripción */}
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT("Descripción")}:</Label>
                            <Input 
                                value={newDescription} 
                                onInput={(e) => setNewDescription(e.target.value)}
                                placeholder="Ingrese descripción"
                            />
                        </div>

                        {/* Instrucciones de despacho */}
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT("Instrucciones de despacho")}:</Label>
                            <Input 
                                value={newShipmentInstructions} 
                                onInput={(e) => setNewShipmentInstructions(e.target.value)}
                                placeholder="Instrucciones especiales"
                            />
                        </div>

                        {/* Guía de despacho */}
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT("Guía de despacho")}:</Label>
                            <Input 
                                value={newSlipNumber}
                                onInput={(e) => {
                                    // Permitir solo números
                                    const value = e.target.value.replace(/\D/g, '');
                                    setNewSlipNumber(value);
                                }}
                                placeholder="Número de guía"
                                style={{ textAlign: 'right' }}
                            />
                        </div>



                        {/* Número de bultos */}
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT("Número de bultos")}:</Label>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Text style={{ margin: '0 10px' }}>{bultos.length}</Text>
                                <Button onClick={handleAddBulto}>+</Button>
                            </div>
                        </div>

                        {/* Placa vehículo */}
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT("Placa vehículo")}:</Label>
                            <Input 
                                value={asnDetail?.vehiclePlate} 
                                onInput={(e) => setAsnDetail({ ...asnDetail, vehiclePlate: e.target.value })}
                                placeholder="Ej: AB123CD"
                            />
                        </div>

                        {/* Volumen */}
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT("Volumen (m³)")}:</Label>
                            <Input 
                                type="text" 
                                value={formatNumberWithComma(newTotalVolume)} 
                                onChange={handleTotalVolumeChange}
                                placeholder="Ingrese volumen total"
                                style={{ textAlign: 'right' }}
                            />
                        </div>

                        {/* Peso */}
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT("Peso (kg)")}:</Label>
                            <Input 
                                type="text" 
                                value={formatNumberWithComma(newTotalWeight)} 
                                onChange={handleTotalWeightChange}
                                placeholder="Ingrese peso total"
                                style={{ textAlign: 'right' }}
                            />
                        </div>

                        {/* Tipo de carga */}
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT("Tipo de carga")}:</Label>
                            <Select
                            onChange={(e) => setNewChargeType(e.detail.selectedOption.dataset.key)}
                            style={{ width: '100%' }}
                        >
                            {chargeTypeOptions.map((option) => (
                                <Option 
                                    key={option.key} 
                                    data-key={option.key}
                                    selected={option.key === newChargeType}
                                >
                                    {option.text}
                                </Option>
                            ))}
                        </Select>
                        </div>

                        {/* Tipo de transporte */}
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT("Tipo de transporte")}:</Label>
                                <Select
                                    onChange={(e) => setNewTransportMedium(e.detail.selectedOption.dataset.key)}
                                    style={{ width: '100%' }}
                                >
                                    {transportMediumOptions.map((option) => (
                                        <Option 
                                            key={option.key} 
                                            data-key={option.key}
                                            selected={option.key === newTransportMedium}
                                        >
                                            {option.text}
                                        </Option>
                                    ))}
                                </Select>
                        </div>

                        {/* Tipo de bulto */}
                        <div>
                            <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>{autoT("Tipo de bulto")}:</Label>
                                <Select
                                    onChange={(e) => setNewShipmentType(e.detail.selectedOption.dataset.key)}
                                    style={{ width: '100%' }}
                                >
                                    {shipmentTypeOptions.map((option) => (
                                        <Option 
                                            key={option.key} 
                                            data-key={option.key}
                                            selected={option.key === newShipmentType}
                                        >
                                            {option.text}
                                        </Option>
                                    ))}
                                </Select>
                           
                        </div>
                    </div>
                <div style={{ padding: '1rem' }}>
                  <Title level="H2" style={{ marginBottom: '1rem' }}>{autoT("Notficaciones de la orden de compra")}</Title>
                  
                  {/* Tabla principal de items */}
                  <div style={{ marginBottom: '2rem' }}>
                    <AnalyticalTable
                      data={purchaseOrder.items || []}
                      columns={itemColumns}
                      selectionMode="None"
                      scaleWidthMode="Smart"
                    />
                  </div>
                </div>
                </WizardStep>

                <WizardStep
                icon="measure"
                titleText={autoT("Editar Bultos")}
                selected={selectedStep === 1}
              >
                <div style={{ 
                      backgroundColor: 'white',
                      padding: '1rem',
                      borderRadius: '8px',
                      boxShadow: '0 0 4px rgba(0,0,0,0.1)'
                    }}>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '1rem' 
                    }}>
                        <Title level="H3">{autoT("Dimensiones de los bultos")}</Title>
                    </div> 

                    {/* Display Total Volume and Weight */}
                    <div style={{ marginBottom: '1rem' }}>
                        <Text>
                            Volumen Total: {formatNumberWithComma(totalBultosVolume)} m³ / {formatNumberWithComma(newTotalVolume)} m³
                        </Text>
                        <Text>
                            Peso Total: {formatNumberWithComma(totalBultosWeight)} kg / {formatNumberWithComma(newTotalWeight)} kg
                        </Text>
                    </div> 
                    
                    {/* Tabla de Bultos (cabecera) */}
                    <AnalyticalTable
                        data={bultos}
                        columns={[
                            { Header: `${autoT('Nro Bulto')}`, accessor: 'packageNumber' },
                            { 
                                Header: `${autoT('Peso - kg')}`,
                                accessor: 'weight',
                                Cell: ({ row }) => (
                                    <Input
                                        type="text"
                                        value={formatNumberWithComma(row.original.weight)}
                                        onChange={(e) => {
                                            const newValue = e.target.value.replace(/[^0-9,]/g, '');
                                            handleBultoUpdate(row.original.packageNumber, 'weight', newValue);
                                        }}
                                        onBlur={(e) => {
                                            const formattedValue = formatNumberWithComma(parseToNumber(e.target.value));
                                            handleBultoUpdate(row.original.packageNumber, 'weight', formattedValue);
                                        }}
                                        style={{ textAlign: 'right' }}
                                    />
                                )
                            },
                            { 
                                Header: `${autoT('Altura - metros')}`,
                                accessor: 'altura',
                                Cell: ({ row }) => (
                                    <Input
                                        type="text"
                                        value={formatNumberWithComma(row.original.altura)}
                                        onChange={(e) => {
                                            // Permitir números, coma y punto (convertir punto a coma)
                                            const newValue = e.target.value.replace(/[^\d,\.]/g, '').replace(/\./g, ',');
                                            // Asegurar que solo hay una coma
                                            const commaCount = (newValue.match(/,/g) || []).length;
                                            if (commaCount <= 1) {
                                                handleBultoUpdate(row.original.packageNumber, 'altura', newValue);
                                            }
                                        }}
                                        onBlur={(e) => {
                                            // Al perder el foco, formatear correctamente
                                            const parsedValue = parseToNumber(e.target.value);
                                            handleBultoUpdate(row.original.packageNumber, 'altura', parsedValue);
                                        }}
                                        style={{ width: '100%', textAlign: 'right' }}
                                    />
                                )
                            },
                            { 
                                Header: `${autoT('Largo - metros')}`,
                                accessor: 'largo',
                                Cell: ({ row }) => (
                                    <Input
                                        type="text"
                                        value={formatNumberWithComma(row.original.largo)}
                                        onChange={(e) => {
                                            // Permitir números, coma y punto (convertir punto a coma)
                                            const newValue = e.target.value.replace(/[^\d,\.]/g, '').replace(/\./g, ',');
                                            // Asegurar que solo hay una coma
                                            const commaCount = (newValue.match(/,/g) || []).length;
                                            if (commaCount <= 1) {
                                                handleBultoUpdate(row.original.packageNumber, 'largo', newValue);
                                            }
                                        }}
                                        onBlur={(e) => {
                                            // Al perder el foco, formatear correctamente
                                            const parsedValue = parseToNumber(e.target.value);
                                            handleBultoUpdate(row.original.packageNumber, 'largo', parsedValue);
                                        }}
                                        style={{ width: '100%', textAlign: 'right' }}
                                    />
                                )
                            },
                            { 
                                Header: `${autoT('Ancho - metros')}`,
                                accessor: 'ancho',
                                Cell: ({ row }) => (
                                    <Input
                                        type="text"
                                        value={formatNumberWithComma(row.original.ancho)}
                                        onChange={(e) => {
                                            // Permitir números, coma y punto (convertir punto a coma)
                                            const newValue = e.target.value.replace(/[^\d,\.]/g, '').replace(/\./g, ',');
                                            // Asegurar que solo hay una coma
                                            const commaCount = (newValue.match(/,/g) || []).length;
                                            if (commaCount <= 1) {
                                                handleBultoUpdate(row.original.packageNumber, 'ancho', newValue);
                                            }
                                        }}
                                        onBlur={(e) => {
                                            // Al perder el foco, formatear correctamente
                                            const parsedValue = parseToNumber(e.target.value);
                                            handleBultoUpdate(row.original.packageNumber, 'ancho', parsedValue);
                                        }}
                                        style={{ width: '100%', textAlign: 'right' }}
                                    />
                                )
                            },
                            {
                                Header: `${autoT('Volumen - m³')}`,
                                accessor: 'volumen',
                                Cell: ({ row }) => (
                                    <Text style={rightAlignedNumberStyle()}>
                                        {formatNumberWithComma(row.original.volumen || 0)}
                                    </Text>
                                ),
                                hAlign: "End"
                            },
                            {
                                Header: `${autoT('Acciones')}`,
                                accessor: 'acciones',
                                Cell: ({ row }) => (
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <Button onClick={() => handleOpenDialog(row.original)}>{autoT("Ver materiales")}</Button>
                                        <Button 
                                            style={{ backgroundColor: 'red', color: 'white' }} 
                                            onClick={() => handleDeleteBulto(row.original)}
                                        >
                                        <Icon name="delete" style={{ color: 'white' }} />
                                        </Button>
                                    </div>
                                )
                            }
                        ]}
                        selectionMode="None"
                        scaleWidthMode="Smart"
                        minRows={Math.min(bultos.length, 1)}
                        visibleRows={Math.min(bultos.length, 10)}
                        style={{ 
                            marginBottom: '1rem' 
                        }}
                        header
                        alternateRowColor
                    />
                </div>
                </WizardStep>
                
                <WizardStep
  icon="attachment"
  titleText={autoT('Adjuntos')}
  selected={selectedStep === 2}
>
  <div style={{ padding: '1rem' }}>
    <Title level="H2" style={{ marginBottom: '1rem' }}>{autoT('Adjuntos')}</Title>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
    {/* Cabecera ASN */}
    <div style={{
            backgroundColor: '#fbbf24',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: 'bold',
            width: 'fit-content' // Limit width to content
          }}>
            {Object.keys(attachments).length}/1 requeridos
          </div>


      {/* Plantilla de Seguridad */}
      <div style={{ 
        border: `1px solid ${attachmentSections.safetySheet?.isRequired ? '#ef4444' : '#3b82f6'}`, 
        borderRadius: '8px', 
        backgroundColor: 'white',
        overflow: 'hidden'
      }}>
        <div 
          onClick={() => toggleAttachmentSection('safetySheet')}
          style={{ 
            padding: '1rem',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: attachments.safetySheet ? '#10b981' : (attachmentSections.safetySheet?.isRequired ? '#fef2f2' : '#eff6ff')
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Text style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {attachments.safetySheet ? <ui5-icon name="checklist-item-2" style={{color: '#10b981'}}></ui5-icon> : <ui5-icon name="attachment"></ui5-icon>} {autoT('Adjunta plantilla de seguridad')}
            </Text>
            {attachments.safetySheet ? (
              <div style={{
                backgroundColor: '#10b981',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {autoT('Completado')}
              </div>
            ) : (
              <div style={{
                backgroundColor: attachmentSections.safetySheet?.isRequired ? '#ef4444' : '#3b82f6',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {attachmentSections.safetySheet?.isRequired ? autoT('Requerido') : autoT('Opcional')}
              </div>
            )}
          </div>
          <Text>{attachmentSections.safetySheet?.isOpen ? '▲' : '▼'}</Text>
        </div>
        
        {attachmentSections.safetySheet?.isOpen && (
          <div style={{ padding: '1rem', borderTop: '1px solid #e5e7eb' }}>
            {attachments.safetySheet ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '1rem',
                backgroundColor: '#f0fdf4',
                border: '1px solid #10b981',
                borderRadius: '6px'
              }}>
                <div>
                  <Text style={{ fontWeight: 'bold', color: '#059669' }}> <ui5-icon name="checklist-item-2" style={{color: '#10b981'}}></ui5-icon> {autoT('Archivo adjuntado')}</Text>
                  <Text style={{ fontSize: '14px', color: '#6b7280' }}>{attachments.safetySheet.name || attachments.safetySheet.file?.name}</Text>
                </div>
                <Button 
                  design="Transparent" 
                  icon="delete"
                  onClick={() => removeAttachment('safetySheet')}
                  style={{ color: '#ef4444' }}
                >
                  {autoT('Remover')}
                </Button>
              </div>
            ) : (
              <div style={{
                border: '2px dashed #d1d5db',
                borderRadius: '8px',
                padding: '2rem',
                textAlign: 'center',
                backgroundColor: '#fafafa'
              }}>
                <div style={{ marginBottom: '1rem' }}>
                <ui5-icon name="folder-full" style={{ width: '3rem', height: '3rem', color: 'orange'}}></ui5-icon>
                </div>
                <Text style={{ fontSize: '16px', marginBottom: '0.5rem' }}>
                  {autoT('Arrastra y suelta tu archivo aquí')}
                </Text>
                <Text style={{ fontSize: '14px', color: '#6b7280', marginBottom: '1rem' }}>
                  {autoT('o selecciona un archivo')}
                </Text>
                <div style={{ 
                  backgroundColor: '#eff6ff', 
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  marginBottom: '1rem',
                  border: '1px solid #3b82f6'
                }}>
                  <Text style={{ fontSize: '12px', color: '#1d4ed8' }}>
                    {autoT('Formatos soportados: PDF, DOC, XLS, JPG, PNG (Máx. 10MB)')}
                  </Text>
                </div>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                  <Button design="Transparent">{autoT('Cancelar')}</Button>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    onChange={async (e) => {
                      if (e.target.files[0]) {
                        await handleAttachmentUpload('safetySheet', e.target.files[0]);
                      }
                    }}
                    style={{ display: 'none' }}
                    id="safetySheet-upload"
                  />
                  <Button 
                    design="Emphasized"
                    onClick={() => document.getElementById('safetySheet-upload').click()}
                  >
                    {autoT('Seleccionar archivo')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Guía de Despacho */}
      <div style={{ 
        border: '1px solid #ef4444', 
        borderRadius: '8px', 
        backgroundColor: 'white',
        overflow: 'hidden'
      }}>
        <div 
          onClick={() => toggleAttachmentSection('dispatchGuide')}
          style={{ 
            padding: '1rem',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: attachments.dispatchGuide ? '#10b981' : '#fef2f2'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Text style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {attachments.dispatchGuide ? <ui5-icon name="checklist-item-2" style={{color: '#10b981'}}></ui5-icon> : <ui5-icon name="attachment"></ui5-icon>} {autoT('Adjunta guía despacho')}
            </Text>
            {attachments.dispatchGuide ? (
              <div style={{
                backgroundColor: '#10b981',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px', 
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {autoT('Completado')}
              </div>
            ) : (
              <div style={{
                backgroundColor: '#ef4444',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {autoT('Requerido')}
              </div>
            )}
          </div>
          <Text>{attachmentSections.dispatchGuide?.isOpen ? '▲' : '▼'}</Text>
        </div>
        
        {attachmentSections.dispatchGuide?.isOpen && (
          <div style={{ padding: '1rem', borderTop: '1px solid #e5e7eb' }}>
            {attachments.dispatchGuide ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '1rem',
                backgroundColor: '#f0fdf4',
                border: '1px solid #10b981',
                borderRadius: '6px'
              }}>
                <div>
                  <Text style={{ fontWeight: 'bold', color: '#059669' }}> <ui5-icon name="checklist-item-2" style={{color: '#10b981'}}></ui5-icon> {autoT('Archivo adjuntado')}</Text>
                  <Text style={{ fontSize: '14px', color: '#6b7280' }}>{attachments.dispatchGuide.name || attachments.dispatchGuide.file?.name}</Text>
                </div>
                <Button 
                  design="Transparent" 
                  icon="delete"
                  onClick={() => removeAttachment('dispatchGuide')}
                  style={{ color: '#ef4444' }}
                >
                  {autoT('Remover')}
                </Button>
              </div>
            ) : (
              <div style={{
                border: '2px dashed #d1d5db',
                borderRadius: '8px',
                padding: '2rem',
                textAlign: 'center',
                backgroundColor: '#fafafa'
              }}>
                <div style={{ marginBottom: '1rem' }}>
                <ui5-icon name="folder-full" style={{ width: '3rem', height: '3rem', color: 'orange'}}></ui5-icon>
                </div>
                <Text style={{ fontSize: '16px', marginBottom: '0.5rem' }}>
                  {autoT('Arrastra y suelta tu archivo aquí')}
                </Text>
                <Text style={{ fontSize: '14px', color: '#6b7280', marginBottom: '1rem' }}>
                  {autoT('o selecciona un archivo')}
                </Text>
                <div style={{ 
                  backgroundColor: '#eff6ff', 
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  marginBottom: '1rem',
                  border: '1px solid #3b82f6'
                }}>
                  <Text style={{ fontSize: '12px', color: '#1d4ed8' }}>
                    {autoT('Formatos soportados: PDF, DOC, XLS, JPG, PNG (Máx. 10MB)')}
                  </Text>
                </div>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                  <Button design="Transparent">{autoT('Cancelar')}</Button>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    onChange={async (e) => {
                      if (e.target.files[0]) {
                        await handleAttachmentUpload('dispatchGuide', e.target.files[0]);
                      }
                    }}
                    style={{ display: 'none' }}
                    id="dispatchGuide-upload"
                  />
                  <Button 
                    design="Emphasized"
                    onClick={() => document.getElementById('dispatchGuide-upload').click()}
                  >
                    {autoT('Seleccionar archivo')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Otros */}
      <div style={{ 
        border: '1px solid #3b82f6', 
        borderRadius: '8px', 
        backgroundColor: 'white',
        overflow: 'hidden'
      }}>
        <div 
          onClick={() => toggleAttachmentSection('other')}
          style={{ 
            padding: '1rem',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#eff6ff'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Text style={{ fontSize: '16px', fontWeight: 'bold' }}> <ui5-icon name="attachment"></ui5-icon> {autoT('Otros')}</Text>
            <div style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {autoT('Opcional')}
            </div>
          </div>
          <Text>{attachmentSections.other?.isOpen ? '▲' : '▼'}</Text>
        </div>
        
        {attachmentSections.other?.isOpen && (
          <div style={{ padding: '1rem', borderTop: '1px solid #e5e7eb' }}>
            {attachments.other ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '1rem',
                backgroundColor: '#f0fdf4',
                border: '1px solid #10b981',
                borderRadius: '6px'
              }}>
                <div>
                  <Text style={{ fontWeight: 'bold', color: '#059669' }}> <ui5-icon name="checklist-item-2" style={{color: '#10b981'}}></ui5-icon> {autoT('Archivo adjuntado')}</Text>
                  <Text style={{ fontSize: '14px', color: '#6b7280' }}>{attachments.other.name || attachments.other.file?.name}</Text>
                </div>
                <Button 
                  design="Transparent" 
                  icon="delete"
                  onClick={() => removeAttachment('other')}
                  style={{ color: '#ef4444' }}
                >
                  {autoT('Remover')}
                </Button>
              </div>
            ) : (
              <div style={{
                border: '2px dashed #d1d5db',
                borderRadius: '8px',
                padding: '2rem',
                textAlign: 'center',
                backgroundColor: '#fafafa'
              }}>
                <div style={{ marginBottom: '1rem' }}>
                <ui5-icon name="folder-full" style={{ width: '3rem', height: '3rem', color: 'orange'}}></ui5-icon>
                </div>
                <Text style={{ fontSize: '16px', marginBottom: '0.5rem' }}>
                  {autoT('Arrastra y suelta tu archivo aquí')}
                </Text>
                <Text style={{ fontSize: '14px', color: '#6b7280', marginBottom: '1rem' }}>
                  {autoT('o selecciona un archivo')}
                </Text>
                <div style={{ 
                  backgroundColor: '#eff6ff', 
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  marginBottom: '1rem',
                  border: '1px solid #3b82f6'
                }}>
                  <Text style={{ fontSize: '12px', color: '#1d4ed8' }}>
                    {autoT('Formatos soportados: PDF, DOC, XLS, JPG, PNG (Máx. 10MB)')}
                  </Text>
                </div>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                  <Button design="Transparent">{autoT('Cancelar')}</Button>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    onChange={async (e) => {
                      if (e.target.files[0]) {
                        await handleAttachmentUpload('other', e.target.files[0]);
                      }
                    }}
                    style={{ display: 'none' }}
                    id="other-upload"
                  />
                  <Button 
                    design="Emphasized"
                    onClick={() => document.getElementById('other-upload').click()}
                  >
                    {autoT('Seleccionar archivo')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
</WizardStep>

                </Wizard>
                <div style={{ 
                    position: 'sticky',
                    bottom: '20px',
                    marginTop: '2rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1rem',
                    backgroundColor: 'white',
                    boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
                    zIndex: 100
                }}>
                    <div style={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: '10px'
                    }}>
                        <Button 
                            onClick={() => setSelectedStep(prevStep => prevStep - 1)}
                            design="Emphasized"
                            disabled={selectedStep === 0}
                        >
                            {autoT("Anterior")}
                        </Button>
                        
                        <Button 
                            onClick={() => setSelectedStep(prevStep => prevStep + 1)}
                            design="Emphasized"
                            disabled={selectedStep === 2} // Assuming there are 3 steps: 0, 1, 2
                        >
                            {autoT("Siguiente")}
                        </Button>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
                <Button 
                    onClick={handleSaveDraft}
                    design="Default"
                    icon="save"
                >
                    {autoT("Guardar borrador")}
                </Button>
                <Button 
                    onClick={handleSaveNotification}
                    design="Positive"
                    icon="save"
                >
                    {autoT("Guardar notificación")}
                </Button>
            </div>
                </div>

                {isDialogOpen && (
                    <Dialog 
                        open={isDialogOpen} 
                        onClose={handleCloseDialog}
                        style={{ width: '80%', maxWidth: '800px' }}
                    >
                        <Title level="H3">{autoT("Materiales para")}: {selectedBulto?.packageNumber}</Title>

                        <AnalyticalTable
                    data={combinedPackingLists.filter(
                        (item) => item.packageNumber === selectedBulto?.packageNumber
                    )}
                            columns={[
                        { Header: 'Posición', accessor: 'lineId' },
                        { Header: 'Código de material', accessor: 'itemDescription' },
                        {
                        Header: 'Nombre',
                        accessor: 'materialCode',
                        Cell: ({ row }) => {
                            const material = asnDetail.items.find(
                            (item) => item.lineId === row.original.lineId
                            );
                            return material ? material.description : 'No disponible';
                        },
                        },
                                {
                                    Header: 'Cantidad',
                                    accessor: 'quantity',
                                    Cell: ({ row }) => {
                            const [tempQuantity, setTempQuantity] = useState(
                            formatNumberWithComma(row.original.quantity)
                            );

                                        return (
                                            <Input
                                type="text"
                                                value={tempQuantity}
                                                onInput={(e) => {
                                const raw = e.target.value;
                                const cleaned = raw.replace(/[^0-9,]/g, '');
                                setTempQuantity(cleaned);
                                                }}
                                                onBlur={() => {
                                const newValue = parseToNumber(tempQuantity);
                                const previousValue = parseToNumber(row.original.quantity);
                                const difference = newValue - previousValue;

                                const updatedPackingLists = combinedPackingLists.map((item) => {
                                    if (
                                    (item.lineId === row.original.lineId) &&
                                    item.packageNumber === row.original.packageNumber
                                    ) {
                                                            return { ...item, quantity: newValue };
                                                        }
                                                        return item;
                                                    });
                                                    setCombinedPackingLists(updatedPackingLists);

                                setDisponibles((prev) => {
                                    let updated = prev.map((item) => {
                                    if (item.lineId === row.original.lineId) {
                                        return {
                                        ...item,
                                        quantityLeft: item.quantityLeft - difference,
                                        };
                                    }
                                    return item;
                                    });

                                    // Agregar si no existe
                                    if (
                                    !updated.some(
                                        (item) => item.lineId === row.original.lineId 
                                    )
                                    ) {
                                    updated.push({
                                        lineId: row.original.lineId,
                                        materialCode: row.original.itemDescription,
                                        quantityLeft: -difference,
                                    });
                                    }

                                    return updated;
                                });

                                setTempQuantity(formatNumberWithComma(newValue));
                                                }}
                                                style={{ width: '100%', textAlign: 'right' }}
                                            />
                                        );
                        },
                                hAlign: "End"
                                },
                            ]}
                            selectionMode="None"
                            scaleWidthMode="Smart"
                            style={{ height: '200px' }}
                        />
                        
                        <Title level="H4">{autoT("Agregar Materiales Disponibles")}</Title>

                        <AnalyticalTable
                    data={disponibles}
                            columns={[
                                { Header: 'Posición', accessor: 'lineId' },
                                { Header: 'Código de material', accessor: 'materialCode' },
                                {
                        Header: 'Nombre',
                        accessor: 'description',
                        Cell: ({ row }) => {
                            const material = asnDetail.items.find(
                            (item) => item.lineId === row.original.lineId 
                            );
                            return material ? material.description : 'No disponible';
                        },
                        },
                        {
                            Header: 'Cantidad faltante',
                            accessor: 'quantityLeft',
                            Cell: ({ row }) => {
                              let number = row.original.quantityLeft ?? 0;
                          
                              // Evitar mostrar -0.000, usar 0.000 en su lugar si está muy cerca de cero
                              if (Math.abs(number) < 0.0005) {
                                number = 0;
                              }
                          
                              return (
                                <Text style={rightAlignedNumberStyle()}>
                                  {number.toLocaleString('es-CL', {
                                    minimumFractionDigits: 3,
                                    maximumFractionDigits: 3
                                  })}
                                </Text>
                              );
                            },
                            hAlign: "End"
                          },
                                                  {
                        Header: 'Acciones',
                        accessor: 'acciones',
                        Cell: ({ row }) => (
                            <Button onClick={() => handleAddMaterial(row.original)}>{autoT("Agregar")}</Button>
                        ),
                        },
                            ]}
                            selectionMode="None"
                            scaleWidthMode="Smart"
                            style={{ height: '200px' }}
                        />
                        
                        <Button onClick={handleCloseDialog}>{autoT("Cerrar")}</Button>
                    </Dialog>
                )}


                </div>
            </div>
            {isErrorDialogOpen && (
                <Dialog 
                    open={isErrorDialogOpen} 
                    onClose={() => setIsErrorDialogOpen(false)} 
                    style={{ width: '80%', maxWidth: '400px' }}
                >
                    <Title level="H3">{autoT("Error")}</Title>
                    <Text>{errorDialogMessage}</Text>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                        <Button onClick={() => setIsErrorDialogOpen(false)}>{autoT("Cerrar")}</Button>
                    </div>
                </Dialog>
            )}
            
            {isSuccessDialogOpen && (
                <Dialog 
                    open={isSuccessDialogOpen} 
                    onClose={() => setIsSuccessDialogOpen(false)} 
                    style={{ width: '80%', maxWidth: '400px' }}
                >
                    <Title level="H3">{autoT("Éxito")}</Title>
                    <Text>{errorDialogMessage}</Text>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                        <Button onClick={() => setIsSuccessDialogOpen(false)}>{autoT("Cerrar")}</Button>
                    </div>
                </Dialog>
            )}
        </div>
    );
};

export default ASNDraft; 